import classnames from 'classnames/bind';
import { useLocation, useNavigate } from 'react-router-dom';

import { Deployment } from 'types/deployment';
import Pipeline from 'types/pipeline';
import { Gateway } from 'types/gateway';

import { relativeTimeFromNow } from 'services/date';
import { truncateMiddle } from 'services/string';
import { useDeleteDeployments } from 'deployments/hooks/useDeleteDeployments';
import { useHasAccess } from 'hooks/useHasAccess';
import { useIsMounted } from 'hooks/useIsMounted';
import { useModal } from 'hooks/useModal';
import { useToggleDeploymentState } from 'deployments/hooks/useDeployment';

import { AppLink } from 'components/AppLink/AppLink';
import { ContextMenu } from 'components/ContextMenu/ContextMenu';
import { ContextMenuAction } from 'components/ContextMenu/ContextMenuAction';
import { Heading } from 'components/Heading/Heading';
import { Icon } from 'components/Icon/IconDeprecated';
import { IconButton } from 'components/IconButton/IconButtonDeprecated';
import { StatusIndicator } from 'components/StatusIndicator/StatusIndicator';
import { Text } from 'components/Text/Text';
import { OptionalTooltip } from 'components/Tooltip/OptionalTooltip';

import { MediaPreview } from 'components/MediaPreview/MediaPreview';
import { useOutputStreams } from 'streams/hooks/useOutputStreams';
import styles from './ItemCompact.module.scss';

const c = classnames.bind(styles);

type DeploymentListItemCompactProps = {
  deployment: Deployment;
  gateway?: Gateway;
  pipeline?: Pipeline;
  isReferenceDeployment?: boolean;
  hideControls?: boolean;
};

export function DeploymentListItemCompact({
  deployment,
  gateway,
  pipeline,
  isReferenceDeployment,
  hideControls,
}: DeploymentListItemCompactProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const timeFromNow = relativeTimeFromNow(deployment.updated_at);

  const { open } = useModal();

  const [hasAccess] = useHasAccess();

  const isMounted = useIsMounted();
  const { toggleState, isLoading } = useToggleDeploymentState();

  const { mutateAsync: deleteDeployments } = useDeleteDeployments();

  const { data: outputStreams } = useOutputStreams({
    deployment_ids: [deployment.id],
  });

  function handleDeleteDeployment() {
    open('confirm-delete', {
      title: `Are you sure you want to delete ${deployment.name}?`,
      onConfirm: () => deleteDeployments({ deployment_ids: [deployment.id] }),
    });
  }

  function handleEditConfiguration() {
    navigate(`${pathname}/edit/${deployment.id}`);
  }

  function handleUpdateState() {
    if (!deployment || !isMounted()) {
      return;
    }

    toggleState(deployment);
  }

  const isActive =
    deployment.state === 'deploying' ||
    deployment.state === 'running' ||
    deployment.state === 'restart_waiting';

  return (
    <div className={c('item')}>
      <div className={c('item-stream')}>
        <MediaPreview
          className={c('stream-thumbnail')}
          stream={outputStreams?.data[0]}
        />
      </div>
      <div className={c('item-info')}>
        <OptionalTooltip
          showTooltip={deployment.name ? deployment.name.length > 80 : false}
          content={deployment.name}
        >
          <Heading level="4" asChild>
            {!hideControls ? (
              <AppLink
                className={c('item-link')}
                to={`${pathname}/ref/${deployment.id}`}
                state={
                  isReferenceDeployment && {
                    defaultTab: 'configuration',
                  }
                }
                keepSearchParams
              >
                {truncateMiddle(deployment.name || 'Unknown', 80)}
              </AppLink>
            ) : (
              <AppLink
                className={c('item-link')}
                to={`${pathname}/edit/${deployment.id}`}
                keepSearchParams
              >
                {truncateMiddle(deployment.name || 'Unknown', 80)}
              </AppLink>
            )}
          </Heading>
        </OptionalTooltip>
        {!hideControls && (
          <div className={c('status')}>
            <div>
              <StatusIndicator status={deployment.state} />
            </div>
            {hasAccess('deploy_edit') && (
              <div className={c('icon')}>
                <IconButton
                  className={c('control')}
                  icon={isActive ? 'stop' : 'play'}
                  label={isActive ? 'stop' : 'start'}
                  onClick={handleUpdateState}
                  loading={isLoading}
                  size="small"
                  variant="secondary"
                  showLabel
                />
              </div>
            )}
          </div>
        )}
      </div>
      <div className={c('entities')}>
        <div>
          <Icon name="pipeline" size="small" />
          {pipeline ? (
            <OptionalTooltip
              showTooltip={pipeline.name.length > 32}
              content={pipeline.name}
            >
              <AppLink
                className={c('link')}
                to={`/design/pipelines/${deployment.pipeline_id}`}
              >
                {truncateMiddle(pipeline.name || 'Unknown', 32)}
              </AppLink>
            </OptionalTooltip>
          ) : (
            <Text className={c('skeleton-text')}>Loading workflow...</Text>
          )}
        </div>

        <div>
          <Icon name="gateway" size="small" />
          {gateway ? (
            <OptionalTooltip
              showTooltip={gateway.name.length > 32}
              content={gateway.name}
            >
              <AppLink
                className={c('link')}
                to={`/deploy/gateways/${gateway.id}`}
              >
                {truncateMiddle(gateway.name || 'Unknown', 32)}
              </AppLink>
            </OptionalTooltip>
          ) : (
            <Text className={c('skeleton-text')}>Loading gateway...</Text>
          )}
        </div>
      </div>
      <div className={c('time')}>
        <Text>{timeFromNow}</Text>
      </div>
      {hasAccess('deploy_edit') && (
        <ContextMenu
          trigger={
            <IconButton
              className={c('control')}
              icon="more-vertical"
              label="Reveal more actions"
              variant="ghost"
            />
          }
        >
          <ContextMenuAction onClick={handleEditConfiguration} icon="edit">
            Edit configuration
          </ContextMenuAction>
          {!hideControls && (
            <ContextMenuAction
              onClick={handleDeleteDeployment}
              icon="delete"
              intent="danger"
            >
              Delete
            </ContextMenuAction>
          )}
        </ContextMenu>
      )}
    </div>
  );
}
